"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Send } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    zip: "",
    project_type: "Home Addition",
    timing: "",
    budget: "",
    message: "",
    consent: false
  });
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setFormData(prev => ({
      ...prev,
      zip: val
    }));
    if (zipError && /^\d{5}$/.test(val)) {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    let hasError = false;
    if (!/^\d{5}$/.test(formData.zip)) {
      setZipError("Please enter a valid 5-digit US ZIP code.");
      hasError = true;
    } else {
      setZipError("");
    }
    if (!formData.consent) {
      setConsentError(true);
      hasError = true;
    } else {
      setConsentError(false);
    }
    if (hasError) return;
    setIsSubmitting(true);
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const bodyParams = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        project_type: formData.project_type,
        address: formData.zip,
        zip: formData.zip,
        details: formData.message,
        timing: formData.timing,
        budget: formData.budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: pageUrl,
        form_location: "home-additions-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: bodyParams
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground font-semibold">
              Request an Estimate
            </h2>
            <div className="w-16 h-0.5 bg-primary mx-auto mt-4 mb-4" aria-hidden="true" />
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
              Tell us about your home addition or remodeling project to schedule
              an initial architectural consultation and pricing review.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitSuccess ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.4
          }} className="py-12 px-4 text-center">
                <div className="size-16 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
                  Thank You for Your Request
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base leading-relaxed">
                  We have received your project details. Our renovation
                  specialists will review your specifications and contact you
                  promptly by phone or email.
                </p>
              </motion.div> : <motion.form key="form" onSubmit={handleSubmit} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" noValidate>
                {errorMessage && <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission Error</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="full-name" className="text-foreground">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="full-name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={e => setFormData(prev => ({
                  ...prev,
                  name: e.target.value
                }))} className="bg-card text-foreground" />
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label htmlFor="phone-number" className="text-foreground">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="phone-number" type="tel" required placeholder="(404) 555-0199" value={formData.phone} onChange={e => setFormData(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} className="bg-card text-foreground" />
                  </div>

                  {/* Email Address */}
                  <div className="space-y-2">
                    <Label htmlFor="email-address" className="text-foreground">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="email-address" type="email" required placeholder="jane@example.com" value={formData.email} onChange={e => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))} className="bg-card text-foreground" />
                  </div>

                  {/* ZIP Code */}
                  <div className="space-y-2">
                    <Label htmlFor="zip-code" className="text-foreground">
                      ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="zip-code" type="text" required placeholder="30319" maxLength={5} value={formData.zip} onChange={handleZipChange} aria-invalid={!!zipError} className="bg-card text-foreground" />
                    {zipError && <p className="text-destructive text-xs font-medium mt-1">
                        {zipError}
                      </p>}
                  </div>

                  {/* Project Type */}
                  <div className="space-y-2">
                    <Label htmlFor="project-type" className="text-foreground">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <select id="project-type" required disabled value={formData.project_type} onChange={e => setFormData(prev => ({
                    ...prev,
                    project_type: e.target.value
                  }))} className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-card px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-75 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                        {PROJECT_TYPE_OPTIONS.map((opt, i) => <option key={i} data-index={i} value={opt}>
                            {opt}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  {/* Preferred Timing */}
                  <div className="space-y-2">
                    <Label htmlFor="preferred-timing" className="text-foreground">
                      Preferred Timing
                    </Label>
                    <select id="preferred-timing" value={formData.timing} onChange={e => setFormData(prev => ({
                  ...prev,
                  timing: e.target.value
                }))} className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-card px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                      <option value="">Select timeframe (optional)</option>
                      {TIMING_OPTIONS.map((timing, i) => <option key={i} data-index={i} value={timing}>
                          {timing}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Budget Range */}
                <div className="space-y-2">
                  <Label htmlFor="budget-range" className="text-foreground">
                    Budget Range
                  </Label>
                  <select id="budget-range" value={formData.budget} onChange={e => setFormData(prev => ({
                ...prev,
                budget: e.target.value
              }))} className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-card px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                    <option value="">Select estimated budget (optional)</option>
                    {BUDGET_OPTIONS.map((budget, i) => <option key={i} data-index={i} value={budget}>
                        {budget}
                      </option>)}
                  </select>
                </div>

                {/* Project Details / Message */}
                <div className="space-y-2">
                  <Label htmlFor="project-details" className="text-foreground">
                    Project Details / Message
                  </Label>
                  <Textarea id="project-details" rows={4} placeholder="Provide any specific dimensions, architectural styles, or goals for your home addition..." value={formData.message} onChange={e => setFormData(prev => ({
                ...prev,
                message: e.target.value
              }))} className="bg-card text-foreground resize-y min-h-[100px]" />
                </div>

                {/* Consent Checkbox */}
                <div className="space-y-1 pt-1">
                  <div className="flex items-start gap-3">
                    <Checkbox id="consent-checkbox" checked={formData.consent} onCheckedChange={checked => {
                  const val = checked === true;
                  setFormData(prev => ({
                    ...prev,
                    consent: val
                  }));
                  if (val && consentError) setConsentError(false);
                }} className="mt-0.5" />
                    <Label htmlFor="consent-checkbox" className="text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                      I agree to be contacted about my project by phone, text, or
                      email.
                    </Label>
                  </div>
                  {consentError && <p className="text-destructive text-xs font-medium ml-7">
                      Please agree to the contact terms to proceed.
                    </p>}
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 h-11">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        Submitting Request...
                      </> : <>
                        <Send className="size-4" />
                        Request Estimate
                      </>}
                  </Button>
                </div>
              </motion.form>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}